import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Alert, Spinner, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PublicLayout from '#components/PublicLayout';
import PublicSharedDataView from '#components/shared-access/PublicSharedDataView';
import { useLazyFetchSharedDataQuery, useResendVerificationCodeMutation } from '#store';

const PublicSharedAccessPage = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [allowedEmail, setAllowedEmail] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const cooldownIntervalRef = useRef(null);

  const [fetchSharedData, { data, isLoading, error, isFetching }] = useLazyFetchSharedDataQuery();
  const [resendCode] = useResendVerificationCodeMutation();
  
  // Ensure language is detected from browser settings for public pages
  useEffect(() => {
    // Detect language from browser
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const detectedLang = browserLang.startsWith('pl') ? 'pl' : 'en';
    
    // Change language if different from detected (LanguageDetector should handle this, but ensure it works)
    if (i18n.language !== detectedLang) {
      i18n.changeLanguage(detectedLang);
    }
  }, []); // Only run once on mount

  const loadSharedData = useCallback(async (code = null) => {
    try {
      setVerificationError(null);
      const result = await fetchSharedData({ token, code }).unwrap();
      // If we got data successfully, verification is not required or already passed
      setRequiresVerification(false);
    } catch (err) {
      // Check if backend requires email verification
      if (err?.status === 403) {
        if (err?.data?.requires_verification) {
          // Email verification required
          setRequiresVerification(true);
          setAllowedEmail(err.data.allowed_email);
          setVerificationError(null);
        } else {
          // Other 403 error (e.g., invalid code)
          setVerificationError(err?.data?.detail || t('shared-access.email-verification.code-error'));
        }
      } else {
        // Other errors will be handled by the error display
        // Don't set requiresVerification to false here, let error display handle it
      }
    }
  }, [token, fetchSharedData, t]);

  const verifyAndLoadData = useCallback(async (code) => {
    try {
      setVerifying(true);
      setVerificationError(null);
      
      const result = await fetchSharedData({ token, code }).unwrap();
      
      // Success - update URL and load data
      setSearchParams({ code });
      setRequiresVerification(false);
      setVerifying(false);
    } catch (err) {
      if (err?.status === 403) {
        setVerificationError(err?.data?.detail || t('shared-access.email-verification.code-error'));
      } else {
        setVerificationError(t('shared-access.email-verification.code-error'));
      }
      setVerifying(false);
    }
  }, [token, fetchSharedData, setSearchParams, t]);

  // Check if code is already in URL and load data
  useEffect(() => {
    if (!token) return;
    // Skip if we already have data
    if (data) return;
    // Skip if we're currently verifying
    if (verifying) return;

    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      // If code is in URL, try to verify with it
      setVerificationCode(codeFromUrl);
      verifyAndLoadData(codeFromUrl);
    } else {
      // No code in URL - try to load data (will trigger verification if needed)
      loadSharedData();
    }
  }, [token, searchParams, loadSharedData, verifyAndLoadData, data, verifying]);

  const handleVerificationSubmit = (e) => {
    e.preventDefault();
    if (verificationCode.length === 6) {
      verifyAndLoadData(verificationCode);
    } else {
      setVerificationError(t('shared-access.email-verification.code-length-error'));
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits
    setVerificationCode(value);
    setVerificationError(null);
  };

  const handleResendCode = useCallback(async () => {
    try {
      setResending(true);
      setResendSuccess(false);
      setVerificationError(null);
      
      await resendCode({ token }).unwrap();
      
      setResendSuccess(true);
      
      // Set cooldown timer (60 seconds)
      setResendCooldown(60);
      
      // Clear any existing interval
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      
      // Start countdown
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
      
    } catch (err) {
      setVerificationError(
        err?.data?.detail || t('shared-access.email-verification.resend-error')
      );
    } finally {
      setResending(false);
    }
  }, [token, resendCode, t]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  const currentLanguage = i18n.language;
  const dateFormat = currentLanguage === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm';

  // Show loading only if we don't have data yet (initial load), not during background refetch
  if (isLoading || (!data && isFetching && !requiresVerification)) {
    return (
      <PublicLayout>
        <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
          <Spinner />
        </div>
      </PublicLayout>
    );
  }

  // Show verification form if required
  if (requiresVerification) {
    return (
      <PublicLayout>
        <Card border='0' className='shadow' style={{ maxWidth: '500px', margin: '3rem auto' }}>
          <Card.Header>
            <Card.Title className='mb-0'>{t('shared-access.email-verification.title')}</Card.Title>
          </Card.Header>
          <Card.Body>
            <p className='mb-2'>
              {t('shared-access.email-verification.info')} <strong>{allowedEmail}</strong>
            </p>
            <p className='text-muted mb-4'>{t('shared-access.email-verification.instructions')}</p>
            
            <Form onSubmit={handleVerificationSubmit}>
              <Form.Group className='mb-3'>
                <Form.Label>{t('shared-access.email-verification.code-label')}</Form.Label>
                <Form.Control
                  type='text'
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder={t('shared-access.email-verification.code-placeholder')}
                  maxLength={6}
                  className={`text-center ${verificationError ? 'is-invalid' : ''}`}
                  style={{
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace',
                  }}
                  autoComplete='off'
                  autoFocus
                />
                {verificationError && (
                  <div className='invalid-feedback d-block'>{verificationError}</div>
                )}
              </Form.Group>
              
              <Button
                type='submit'
                variant='primary'
                disabled={verificationCode.length !== 6 || verifying}
                className='w-100'
              >
                {verifying ? t('shared-access.email-verification.verifying') : t('shared-access.email-verification.verify-button')}
              </Button>
            </Form>
            
            {resendSuccess && (
              <Alert variant='success' className='mt-3 mb-0'>
                {t('shared-access.email-verification.resend-success', { email: allowedEmail })}
              </Alert>
            )}
            
            <div className='mt-4 pt-3 border-top text-center'>
              <p className='text-muted mb-2'>{t('shared-access.email-verification.resend-question')}</p>
              <Button
                type='button'
                variant='outline-primary'
                onClick={handleResendCode}
                disabled={resending || resendCooldown > 0}
                className='w-100'
              >
                {resending 
                  ? t('shared-access.email-verification.resending')
                  : resendCooldown > 0 
                    ? t('shared-access.email-verification.resend-cooldown', { seconds: resendCooldown })
                    : t('shared-access.email-verification.resend-button')
                }
              </Button>
            </div>
            
            <div className='mt-4 pt-3 border-top'>
              <p className='text-muted mb-2'>{t('shared-access.email-verification.help-title')}</p>
              <ul className='text-muted small mb-0'>
                <li>{t('shared-access.email-verification.help-1')}</li>
                <li>{t('shared-access.email-verification.help-2')}</li>
                <li>{t('shared-access.email-verification.help-3')}</li>
              </ul>
            </div>
          </Card.Body>
        </Card>
      </PublicLayout>
    );
  }

  if (error && !requiresVerification) {
    let errorMessage = t('shared-access.link-not-found');
    if (error?.status === 404) {
      const detail = error?.data?.detail || '';
      // Check if link was deleted (soft delete)
      if (detail.toLowerCase().includes('deleted') || detail.toLowerCase().includes('usunięt')) {
        errorMessage = t('shared-access.link-deleted');
      } else {
        errorMessage = detail || t('shared-access.link-not-found');
      }
    } else if (error?.data?.detail) {
      errorMessage = error.data.detail;
    }

    return (
      <PublicLayout>
        <Card border='0' className='shadow'>
          <Card.Body>
            <Alert variant='danger'>
              <h5 className='mb-3'>{t('shared-access.error-title')}</h5>
              <p className='mb-0'>{errorMessage}</p>
            </Alert>
          </Card.Body>
        </Card>
      </PublicLayout>
    );
  }

  if (!data) {
    return null;
  }

  const { patient, link_info, allowed_scopes, data: sharedData, snapshot_info } = data;

  return (
    <PublicLayout>
      {/* Informacja o snapshotie */}
      {snapshot_info && snapshot_info.is_snapshot && (
        <Card border='0' className='shadow mb-4' style={{ borderLeft: '4px solid #2196f3' }}>
          <Card.Body style={{ backgroundColor: '#e3f2fd', padding: '12px' }}>
            <p className='mb-0' style={{ color: '#1976d2', fontSize: '14px', margin: 0 }}>
              <strong>{t('shared-access.public-view.snapshot-warning')}:</strong>{' '}
              {t('shared-access.public-view.snapshot-message', {
                date: moment(snapshot_info.snapshot_created_at).format(dateFormat)
              })}
            </p>
          </Card.Body>
        </Card>
      )}

      <Card border='0' className='shadow mb-4'>
        <Card.Header>
          <Card.Title className='mb-0'>{t('shared-access.public-view.title')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className='mb-4'>
            <h5>{t('shared-access.public-view.patient-info')}</h5>
            <div className='ms-3'>
              <p className='mb-1'>
                <strong>{t('shared-access.public-view.patient-name')}:</strong> {patient?.name || '-'}
              </p>
              <p className='mb-0'>
                <strong>{t('shared-access.public-view.patient-email')}:</strong> {patient?.email || '-'}
              </p>
            </div>
          </div>

          <div className='mb-4'>
            <h5>{t('shared-access.public-view.link-info')}</h5>
            <div className='ms-3'>
              <p className='mb-1'>
                <strong>{t('shared-access.created')}:</strong>{' '}
                {moment(link_info?.created).format(dateFormat)}
              </p>
              <p className='mb-1'>
                <strong>{t('shared-access.expires-at')}:</strong>{' '}
                {moment(link_info?.expires_at).format(dateFormat)}
              </p>
              <p className='mb-0'>
                <strong>{t('shared-access.access-count')}:</strong> {link_info?.access_count || 0}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card border='0' className='shadow mb-4'>
        <Card.Header>
          <Card.Title className='mb-0'>{t('shared-access.public-view.shared-data-title')}</Card.Title>
        </Card.Header>
            <Card.Body>
              <PublicSharedDataView data={sharedData} allowedScopes={allowed_scopes} token={token} />
            </Card.Body>
      </Card>
    </PublicLayout>
  );
};

export default PublicSharedAccessPage;

